import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BookOpen, Search, Filter, Clock, Users, Star, 
  ChevronRight, GraduationCap, Layers, Code, Brain,
  Cloud, Database, Sparkles, Play, CheckCircle, Lock
} from 'lucide-react';

const categoryIcons = {
  full_stack_development: Code,
  ai_machine_learning: Brain,
  generative_ai: Sparkles,
  cloud_devops: Cloud,
  data_science: Database,
  cybersecurity: Lock,
  mobile_development: Layers,
  other: BookOpen
};

const categoryColors = {
  full_stack_development: 'bg-blue-500/10 text-blue-600 border-blue-200',
  ai_machine_learning: 'bg-purple-500/10 text-purple-600 border-purple-200',
  generative_ai: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
  cloud_devops: 'bg-orange-500/10 text-orange-600 border-orange-200',
  data_science: 'bg-cyan-500/10 text-cyan-600 border-cyan-200',
  cybersecurity: 'bg-red-500/10 text-red-600 border-red-200',
  mobile_development: 'bg-pink-500/10 text-pink-600 border-pink-200',
  other: 'bg-gray-500/10 text-gray-600 border-gray-200'
};

const difficultyColors = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700'
};

const formatCategoryName = (category) => {
  return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// CourseCard component - moved outside to avoid recreating on each render
const CourseCard = ({ course, onNavigate, onEnroll }) => {
  // Support both old tech_courses and new Course Builder courses
  const category = course.category || course.category_name || 'other';
  const categoryKey = category.toLowerCase().replace(/[^a-z]/g, '_');
  const CategoryIcon = categoryIcons[categoryKey] || BookOpen;
  const colorClass = categoryColors[categoryKey] || categoryColors.other;
  
  // Get progress info from user_progress object (Course Builder) or direct fields
  const progressPercent = course.user_progress?.overall_progress_percent || course.user_progress || 0;
  const isEnrolled = course.user_progress || course.user_enrolled;
  
  // Module count from Course Builder or lesson count from old courses
  const moduleCount = course.module_count || course.lesson_count || 0;
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={() => onNavigate(`/finance-training/builder-courses/${course.id}`)}
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative h-40 overflow-hidden">
        {course.thumbnail_url ? (
          <img 
            src={course.thumbnail_url} 
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${colorClass}`}>
            <CategoryIcon className="h-16 w-16 opacity-50" />
          </div>
        )}
        {course.is_featured && (
          <Badge className="absolute top-2 right-2 bg-amber-500">
            <Star className="h-3 w-3 mr-1" /> Featured
          </Badge>
        )}
        {isEnrolled && progressPercent > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
            <div className="flex items-center gap-2 text-white text-sm">
              <Progress value={progressPercent} className="h-1.5 flex-1 bg-white/30" />
              <span>{Math.round(progressPercent)}%</span>
            </div>
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className={colorClass}>
            <CategoryIcon className="h-3 w-3 mr-1" />
            {course.category_name || formatCategoryName(category)}
          </Badge>
          {course.status === 'published' && (
            <Badge className="bg-green-100 text-green-700">Published</Badge>
          )}
        </div>
        <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
          {course.title}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pb-2">
        <CardDescription className="line-clamp-2">
          {course.description}
        </CardDescription>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          {(course.estimated_hours || course.module_count) && (
            <span className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              {course.module_count ? `${course.module_count} modules` : `${course.estimated_hours}h`}
            </span>
          )}
          {(course.video_count > 0 || course.document_count > 0) && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {course.video_count || 0} videos, {course.document_count || 0} docs
            </span>
          )}
          {course.assessment_count > 0 && (
            <span className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              {course.assessment_count} quiz{course.assessment_count > 1 ? 'zes' : ''}
            </span>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-2">
        {isEnrolled ? (
          <Button className="w-full" variant="outline">
            {progressPercent > 0 ? (
              <>
                <Play className="h-4 w-4 mr-2" /> Continue Learning
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" /> Start Course
              </>
            )}
          </Button>
        ) : (
          <Button className="w-full">
            <Play className="h-4 w-4 mr-2" /> Start Learning
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

const FinanceTrainingLibrary = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Check feature access
  const hasFinanceTrainingAccess = user?.role === 'admin' || user?.feature_access?.finance_training !== false;
  
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [stats, setStats] = useState(null);

  // Redirect if no access
  useEffect(() => {
    if (!hasFinanceTrainingAccess) {
      navigate('/dashboard');
    }
  }, [hasFinanceTrainingAccess, navigate]);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch courses assigned to this user from the Course Builder
      const response = await api.get('/finance-training/builder-courses/my-courses');
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchCategories = useCallback(async () => {
    try {
      // Fetch categories from Course Builder
      const response = await api.get('/finance-training/builder/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/finance-training/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchCourses();
    fetchCategories();
    fetchStats();
  }, [fetchCourses, fetchCategories, fetchStats]);

  const handleEnroll = async (courseId) => {
    try {
      await api.post(`/finance-training/enroll/${courseId}`);
      fetchCourses(); // Refresh to show enrolled status
      fetchStats();
    } catch (error) {
      console.error('Failed to enroll:', error);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="finance-training-library">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-primary" />
              Finance Training Library
            </h1>
            <p className="text-muted-foreground mt-1">
              Master new technologies with expert-led courses
            </p>
          </div>
          
          {stats && (
            <div className="flex gap-4">
              <Card className="px-4 py-2">
                <div className="text-2xl font-bold">{stats.total_enrolled}</div>
                <div className="text-xs text-muted-foreground">Enrolled</div>
              </Card>
              <Card className="px-4 py-2">
                <div className="text-2xl font-bold">{stats.in_progress}</div>
                <div className="text-xs text-muted-foreground">In Progress</div>
              </Card>
              <Card className="px-4 py-2">
                <div className="text-2xl font-bold">{stats.completed}</div>
                <div className="text-xs text-muted-foreground">Completed</div>
              </Card>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="course-search-input"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
              <SelectTrigger className="w-full md:w-[150px]" data-testid="difficulty-filter">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Course Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="h-[380px] animate-pulse">
                <div className="h-40 bg-muted" />
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-6 bg-muted rounded w-full mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-3/4 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : courses.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mt-4">No courses assigned</h3>
            <p className="text-muted-foreground mt-2">
              You don&apos;t have any courses assigned yet. Contact your administrator to get enrolled in courses.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {courses.map(course => (
              <CourseCard key={course.id} course={course} onNavigate={navigate} onEnroll={handleEnroll} />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default FinanceTrainingLibrary;
